/* eslint-disable */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, getDocs, getDocsFromServer, getDoc, doc, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { GLOBAL_CSS, VALID_CATEGORIES, VALID_BOOKING_STATUSES } from "./constants";
import DayPicker from "./DayPicker";
import DayView from "./DayView";
import TripSkeletonApproval from "./TripSkeletonApproval";
import Toast, { useToast } from "./Toast";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";
import useWindowWidth from "../utils/useWindowWidth";
import { countryToFlag } from "../utils/countryFlags";
import { takeSnapshot } from "../utils/snapshots";
import VersionHistory from "./VersionHistory";
import { generateTripActivities } from "../utils/generateTripActivities";
import { getSetting } from "../utils/settings";
import { authenticatedFetch } from "../utils/apiClient";
import TripEditPanel from "./TripEditPanel";
import TripOverflowMenu from "./TripOverflowMenu";
import { resolveDisplayPref } from "../utils/displayPrefs";
import { prepareExportData } from "../utils/prepareExportData";
import { fetchAllExportImages } from "../utils/imageExportUtils";
import { generateExportFileName } from "../utils/exportFileName";

const API_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";

// Compute a short display date ("Apr 26") from trip start + day order
function computeDate(startDateStr, order, displayDate) {
  if (startDateStr) {
    try {
      const base = new Date(startDateStr + "T00:00:00Z");
      if (!isNaN(base)) {
        const d = new Date(base);
        d.setUTCDate(d.getUTCDate() + (order - 1));
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
      }
    } catch { /* fall through */ }
  }
  if (displayDate) {
    const parts = displayDate.split(" ");
    if (parts.length >= 3) return parts.slice(1).join(" ");
    if (parts.length === 2) return displayDate;
  }
  return `Day ${order}`;
}

// Normalize Firestore day doc to the shape components expect
function normalizeDay(data) {
  return {
    ...data,
    day: data.order,         // DayPicker + DayView use d.day for the day number
    date: data.displayDate,  // components display day.date as human-readable string
    activities: [null, null, null, null], // placeholder so DayPicker dot count renders
  };
}

export default function ItineraryView() {
  const { isDesktop } = useWindowWidth();
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [selDay, setSelDay] = useState(0);

  const handleDayUpdate = useCallback((dayId, changes) => {
    setDays(prev => prev.map(d => d.id === dayId ? { ...d, ...changes } : d));
  }, []);

  const [tripStatus, setTripStatus] = useState(undefined); // undefined = not yet loaded
  const [tripStatusLoading, setTripStatusLoading] = useState(true);
  const [tripData, setTripData] = useState(null);
  const [tripNotFound, setTripNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  // Fetch trip doc to check status
  useEffect(() => {
    async function fetchTripStatus() {
      try {
        const snap = await getDoc(doc(db, "trips", tripId));
        if (!snap.exists()) {
          setTripNotFound(true);
          return;
        }
        const data = snap.data();
        setTripData(data);
        setTripStatus(data.status || "ready");
      } catch (e) {
        console.error("[ItineraryView] Failed to fetch trip status:", e);
        setFetchError(true);
      } finally {
        setTripStatusLoading(false);
      }
    }
    fetchTripStatus();
  }, [tripId]);

  const [days, setDays] = useState([]);
  const [daysLoading, setDaysLoading] = useState(true);
  const [daysError, setDaysError] = useState(null);
  const [activityCounts, setActivityCounts] = useState({});
  const [activityTitlesByDay, setActivityTitlesByDay] = useState({});
  const [daysRefreshKey, setDaysRefreshKey] = useState(0);

  useEffect(() => {
    async function fetchDays() {
      setDaysError(null);
      try {
        const q = query(
          collection(db, "days"),
          where("ownerUid", "==", auth.currentUser.uid),
          where("tripId", "==", tripId),
          orderBy("order")
        );
        const snap = await getDocsFromServer(q);
        setDays(snap.docs.map(d => normalizeDay({ ...d.data(), id: d.id })));

        // Fetch activity counts per day (primary only)
        // Query by dayId set (not tripId) to handle mixed-tripId data
        const dayIds = snap.docs.map(d => d.id);
        const counts = {};
        // Firestore 'in' supports up to 30 values — safe for trip days
        if (dayIds.length > 0) {
          const actQ = query(
            collection(db, "activities"),
            where("ownerUid", "==", auth.currentUser.uid),
            where("dayId", "in", dayIds)
          );
          const actSnap = await getDocs(actQ);
          const titlesByDay = {};
          actSnap.docs.forEach(d => {
            const data = d.data();
            if (data.planType === "primary") {
              counts[data.dayId] = (counts[data.dayId] || 0) + 1;
            }
            if (data.title) {
              if (!titlesByDay[data.dayId]) titlesByDay[data.dayId] = [];
              titlesByDay[data.dayId].push(data.title);
            }
          });
          setActivityTitlesByDay(titlesByDay);
        }
        setActivityCounts(counts);
      } catch (e) {
        console.error("[ItineraryView] Failed to fetch days:", e);
        setDaysError(e.message);
      } finally {
        setDaysLoading(false);
      }
    }
    fetchDays();
  }, [tripId, daysRefreshKey]);
  const cfg = {
    travelers:   tripData?.participants || tripData?.brief?.travelers || [],
    departureCity: tripData?.brief?.departureCity || "",
    startDate:   tripData?.startDate  || tripData?.brief?.departureDate || "",
    endDate:     tripData?.endDate    || tripData?.brief?.returnDate || "",
    routing:     tripData?.routing    || tripData?.brief?.destinations || [],
    ferry:       tripData?.brief?.ferry || "",
    accommodationType: tripData?.brief?.accommodationType || "",
    budgetLevel: tripData?.brief?.budget || "",
  };

  const [toast, showToast] = useToast();
  const [showHistory, setShowHistory] = useState(false);
  const [showTripEdit, setShowTripEdit] = useState(false);
  const [showAddDay, setShowAddDay] = useState(false);
  const [addDayForm, setAddDayForm] = useState({ city: "" });
  const [addDayMode, setAddDayMode] = useState("blank"); // "blank" | "ai"
  const [addDayPrompt, setAddDayPrompt] = useState("");
  const [addDayLoading, setAddDayLoading] = useState(false);
  const [addDayClosing, setAddDayClosing] = useState(false);
  const [insertPosition, setInsertPosition] = useState(null);

  const swipeTx = useRef(null);
  const pickerRef = useRef(null);
  const pillRefs = useRef([]);
  const flushNotesRef = useRef(null);
  const generationFiredRef = useRef(false);

  const day = days[selDay];

  // Compute display dates from trip startDate + day order
  const tripStartDate = tripData?.startDate || tripData?.brief?.departureDate || null;
  const daysWithDates = useMemo(() =>
    days.map(d => ({ ...d, computedDate: computeDate(tripStartDate, d.order, d.displayDate) })),
    [days, tripStartDate]
  );

  const handleDeleteDay = useCallback(async (dayId) => {
    const dayToDelete = days.find(d => d.id === dayId);
    if (!dayToDelete) return;
    try {
      takeSnapshot(db, tripId, `Day deleted: ${dayToDelete.city}`, days, []).catch(console.error);
      const batch = writeBatch(db);

      // Delete all activity docs for this day
      const actQ = query(
        collection(db, "activities"),
        where("ownerUid", "==", auth.currentUser.uid),
        where("dayId", "==", dayId)
      );
      const actSnap = await getDocs(actQ);
      actSnap.docs.forEach(d => batch.delete(doc(db, "activities", d.id)));

      // Delete the day doc
      batch.delete(doc(db, "days", dayId));

      // Rewrite order on remaining days
      const remainingDays = days
        .filter(d => d.id !== dayId)
        .sort((a, b) => (a.order || a.day) - (b.order || b.day));
      remainingDays.forEach((d, i) => {
        batch.update(doc(db, "days", d.id), { order: i + 1 });
      });

      await batch.commit();

      const newDays = remainingDays.map((d, i) =>
        ({ ...d, order: i + 1, day: i + 1 }));
      setDays(newDays);
      setSelDay(Math.min(selDay, Math.max(0, newDays.length - 1)));
      showToast("Day deleted", "success");
    } catch (e) {
      console.error("[ItineraryView] Failed to delete day:", e);
      showToast("⚠️ Failed to delete day — " + e.message, "error");
    }
  }, [days, selDay, showToast]);

  const handleMoveDay = useCallback(async (dayId, direction) => {
    const movingDay = days.find(d => d.id === dayId);
    if (!movingDay) return;
    const currentOrder = movingDay.order;
    const targetOrder = direction === "left" ? currentOrder - 1 : currentOrder + 1;
    const targetDay = days.find(d => d.order === targetOrder);
    if (!targetDay) return;

    // Defense-in-depth: same city check
    if (movingDay.city !== targetDay.city) {
      showToast(`Can't move outside the ${movingDay.city} block`, "error");
      return;
    }

    const prevDays = [...days];
    const prevSelDay = selDay;
    const newDays = days.map(d => {
      if (d.id === dayId) return { ...d, order: targetOrder, day: targetOrder };
      if (d.id === targetDay.id) return { ...d, order: currentOrder, day: currentOrder };
      return d;
    }).sort((a, b) => a.order - b.order);

    setDays(newDays);
    const newIdx = newDays.findIndex(d => d.id === dayId);
    if (newIdx >= 0) setSelDay(newIdx);

    takeSnapshot(db, tripId, `Day reorder: ${movingDay.city}`, days, []).catch(console.error);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "days", dayId), { order: targetOrder });
      batch.update(doc(db, "days", targetDay.id), { order: currentOrder });
      await batch.commit();
    } catch (e) {
      setDays(prevDays);
      setSelDay(prevSelDay);
      showToast("Failed to reorder — " + e.message, "error");
    }
  }, [days, selDay, showToast]);

  const goDay = useCallback(async (i) => {
    if (flushNotesRef.current) {
      await flushNotesRef.current();
    }
    setSelDay(i);
    setTimeout(() => {
      const pill = pillRefs.current[i];
      const container = pickerRef.current;
      if (pill && container) {
        const target = pill.offsetLeft - (container.offsetWidth / 2) + (pill.offsetWidth / 2);
        container.scrollTo({ left: target, behavior: "smooth" });
      }
    }, 50);
  }, []);

  const onTS = useCallback(e => { swipeTx.current = e.touches[0].clientX; }, []);
  const onTE = useCallback(e => {
    if (swipeTx.current === null) return;
    const dx = e.changedTouches[0].clientX - swipeTx.current;
    if (Math.abs(dx) > 60) {
      if (dx < 0 && selDay < days.length - 1) goDay(selDay + 1);
      if (dx > 0 && selDay > 0) goDay(selDay - 1);
    }
    swipeTx.current = null;
  }, [selDay, goDay]);

  const closeAddDay = useCallback(() => {
    setAddDayClosing(true);
    setTimeout(() => {
      setShowAddDay(false);
      setAddDayClosing(false);
      setInsertPosition(null);
    }, 260);
  }, []);

  // dayIsPending: only used to gate TripSkeletonApproval wizard + skeleton placeholders in ItineraryView.
  // Does NOT affect DayView activity rendering — DayView shows activities if they exist, regardless of this flag.
  const dayIsPending = tripStatus !== "ready" && day?.aiInitialPassComplete !== true;

  // Poll for day completion when trip is "creating" (Change 5)
  useEffect(() => {
    if (tripStatus !== "creating") return;
    const interval = setInterval(async () => {
      try {
        const tripSnap = await getDoc(doc(db, "trips", tripId));
        if (tripSnap.exists()) {
          const newStatus = tripSnap.data().status || "ready";
          if (newStatus !== "creating") {
            setTripStatus(newStatus);
            setTripData(prev => ({ ...prev, status: newStatus }));
            setDaysRefreshKey(k => k + 1);
            return;
          }
        }
        // Refresh days to pick up aiInitialPassComplete changes
        setDaysRefreshKey(k => k + 1);

        // Self-heal stale "creating" + wave3Started status
        // (fires when browser was closed during background generation)
        if (tripSnap.exists() && tripSnap.data().wave3Started === true) {
          const daysSnap = await getDocs(query(
            collection(db, "days"),
            where("ownerUid", "==", auth.currentUser.uid),
            where("tripId", "==", tripId),
            orderBy("order")
          ));
          const freshDays = daysSnap.docs.map(d => ({ ...d.data(), id: d.id }));
          if (
            freshDays.length > 0 &&
            freshDays.every(d => d.aiInitialPassComplete !== false)
          ) {
            try {
              await updateDoc(doc(db, "trips", tripId), { status: "ready" });
              setTripStatus("ready");
              setTripData(prev => ({ ...prev, status: "ready" }));
            } catch (e) {
              console.error("[ItineraryView] Failed to self-heal trip status", e);
            }
          }
        }
      } catch (_) { /* ignore polling errors */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [tripStatus, tripId]);

  // Trigger background generation when ItineraryView detects wave3Started + creating.
  // This runs on the page that stays open, not the wizard page that's about to be destroyed.
  // Guard: skip if any day already has aiInitialPassComplete set (generation already in progress
  // from fast path in tripCreation.js or a previous ItineraryView mount).
  useEffect(() => {
    if (tripStatus !== "creating" || !tripData?.wave3Started || generationFiredRef.current) return;
    if (daysLoading || days.length === 0) return;
    if (tripData?.generationStartedAt) {
      generationFiredRef.current = true;
      return;
    }
    const alreadyStarted = days.some(d => d.aiInitialPassComplete !== undefined && d.aiInitialPassComplete !== null);
    if (alreadyStarted) {
      generationFiredRef.current = true;
      return;
    }
    generationFiredRef.current = true;

    const triggerGeneration = async () => {
      // Fetch existing activities (Wave 2 must-sees) so generateTripActivities
      // can match them by title and update instead of creating duplicates
      let wave2MustSees = [];
      try {
        const dayIds = days.map(d => d.id);
        if (dayIds.length > 0) {
          const actSnap = await getDocs(query(
            collection(db, "activities"),
            where("ownerUid", "==", auth.currentUser.uid),
            where("dayId", "in", dayIds),
          ));
          wave2MustSees = actSnap.docs.map(d => {
            const data = d.data();
            const dayDoc = days.find(dd => dd.id === data.dayId);
            return {
              ...data,
              id: d.id,
              dayNumber: dayDoc?.order || dayDoc?.day || 1,
            };
          });
        }
      } catch (e) {
        console.error("[ItineraryView] Failed to fetch existing activities for generation:", e);
      }

      const daysForGen = days.map(d => ({
        id: d.id,
        tripId: tripId,
        order: d.order || d.day,
        dayNumber: d.order || d.day,
        city: d.city,
        country: d.country,
        date: d.date || d.displayDate,
        isTransitDay: d.isTransitDay || false,
        transitMode: d.transitMode || null,
      }));

      try {
        generateTripActivities(tripId, daysForGen, {}, API_URL, tripData.brief || {}, wave2MustSees);
      } catch (e) {
        console.error("[ItineraryView] Background generation failed:", e);
      }
    };

    triggerGeneration();
  }, [tripStatus, tripData, days, daysLoading, tripId]);

  const handleCompleteDay = useCallback(async () => {
    if (!day || !tripData) return;
    try {
      await generateTripActivities(
        tripId,
        [day],
        {},
        API_URL,
        tripData.brief,
        []
      );
      setDaysRefreshKey(k => k + 1);
      showToast("Activities generated", "success");
    } catch (e) {
      console.error("[ItineraryView] Complete Day failed", e);
      showToast(`Oopsie, our AI magic couldn't complete ${day.city} \u2014 tap Complete Day to try again.`, "error");
    }
  }, [day, tripData, tripId, showToast]);

  const handleExport = useCallback(async (format) => {
    try {
      showToast('Preparing your export...', 'info');

      // 1. Prepare data — fetch activities for all days
      const dayIds = days.map(d => d.id);
      const activitiesByDay = {};
      if (dayIds.length > 0) {
        const actSnap = await getDocs(query(
          collection(db, "activities"),
          where("ownerUid", "==", auth.currentUser.uid),
          where("dayId", "in", dayIds),
          where("planType", "==", "primary")
        ));
        actSnap.docs.forEach(d => {
          const data = { ...d.data(), id: d.id };
          if (!activitiesByDay[data.dayId]) activitiesByDay[data.dayId] = [];
          activitiesByDay[data.dayId].push(data);
        });
      }

      const displayPrefs = {
        showCostEstimates: resolveDisplayPref('showCostEstimates', tripData),
        showBookingInfo: resolveDisplayPref('showBookingInfo', tripData),
        showLocation: resolveDisplayPref('showLocation', tripData),
      };
      const exportData = prepareExportData(tripData, days, activitiesByDay, displayPrefs);

      // 2. Fetch images
      const imageMap = await fetchAllExportImages(exportData);

      // 3. Lazy-import the correct generator
      let blob;
      if (format === 'pdf') {
        const { generateTripPDF } = await import('../utils/pdfExport');
        blob = await generateTripPDF(exportData, imageMap);
      } else {
        const { generateTripDocx } = await import('../utils/docxExport');
        blob = await generateTripDocx(exportData, imageMap);
      }

      // 4. Trigger download
      const { saveAs } = await import('file-saver');
      const fileName = generateExportFileName(tripData, format);
      saveAs(blob, fileName);

      showToast('Export complete!', 'success');
    } catch (err) {
      console.error('Export failed:', err);
      showToast('Export failed. Please try again.', 'error');
    }
  }, [days, tripData, showToast]);

  const handleAddDay = async () => {
    if (!addDayForm.city.trim()) {
      showToast("City is required", "error");
      return;
    }
    setAddDayLoading(true);
    try {
      const newId = doc(collection(db, "days")).id;
      const effectiveOrder = insertPosition != null ? insertPosition : days.length + 1;

      // Auto-capitalize city: "sOUTH AFRICA" → "South Africa"
      const cityCapitalized = addDayForm.city.trim()
        .split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

      const dayObject = {
        tripId,
        ownerUid: auth.currentUser.uid,
        order: effectiveOrder,
        city: cityCapitalized,
        country: "",
        theme: cityCapitalized.toLowerCase().includes('transit') ? 'Travel Day' : `Day in ${cityCapitalized}`,
        tip: "",
        heroImg: null,
        hidden: false,
        locked: false,
        flag: "",
      };

      takeSnapshot(db, tripId, `Day added: ${cityCapitalized}`, days, []).catch(console.error);

      if (insertPosition != null) {
        // Batch: shift existing days at or after insertPosition, then add new day
        const batch = writeBatch(db);
        days.filter(d => d.order >= insertPosition).forEach(d => {
          batch.update(doc(db, "days", d.id), { order: d.order + 1 });
        });
        batch.set(doc(db, "days", newId), dayObject);
        await batch.commit();
        setDays(prev => {
          const shifted = prev.map(d =>
            d.order >= insertPosition ? { ...d, order: d.order + 1, day: d.order + 1 } : d
          );
          return [...shifted, normalizeDay({ ...dayObject, id: newId })].sort((a, b) => a.order - b.order);
        });
      } else {
        // Append at end
        await setDoc(doc(db, "days", newId), dayObject);
        setDays(prev => [...prev, normalizeDay({ ...dayObject, id: newId })]);
      }

      // AI Generate: call API and write activities + country/flag
      if (addDayMode === "ai") {
        try {
          // Build route context for the AI
          const wave1DaysPayload = days.map(d => ({
            dayNumber: d.order || d.day,
            city: d.city || "",
            country: d.country || "",
            transitMode: null,
            isTransitDay: false,
          }));
          // Include the newly added day
          wave1DaysPayload.push({
            dayNumber: effectiveOrder,
            city: cityCapitalized,
            country: "",
            transitMode: null,
            isTransitDay: false,
          });
          wave1DaysPayload.sort((a, b) => a.dayNumber - b.dayNumber);

          const targetDayPayload = {
            dayNumber: effectiveOrder,
            city: cityCapitalized,
            country: "",
            transitMode: null,
            isTransitDay: false,
            ...(addDayPrompt.trim() ? { userNotes: addDayPrompt.trim() } : {}),
          };

          // Fetch existing activities so the AI avoids duplicates
          const dayIds = days.map(d => d.id);
          let existingActivities = [];
          if (dayIds.length > 0) {
            try {
              // Firestore 'in' supports up to 30 values — chunk if needed
              const chunks = [];
              for (let i = 0; i < dayIds.length; i += 30) {
                chunks.push(dayIds.slice(i, i + 30));
              }
              const allDocs = [];
              for (const chunk of chunks) {
                const actSnap = await getDocs(query(
                  collection(db, "activities"),
                  where("ownerUid", "==", auth.currentUser.uid),
                  where("dayId", "in", chunk),
                ));
                allDocs.push(...actSnap.docs);
              }
              existingActivities = allDocs.map(d => {
                const data = d.data();
                const dayDoc = days.find(dd => dd.id === data.dayId);
                return { ...data, id: d.id, dayNumber: dayDoc?.order || dayDoc?.day || 1 };
              });
            } catch (e) {
              console.error("[handleAddDay] Failed to fetch activities:", e);
            }
          }

          // Build completedDays from days that already have AI-generated activities
          const completedDays = days
            .filter(d => d.aiInitialPassComplete === true)
            .map(d => ({
              dayNumber: d.order || d.dayNumber,
              city: d.city,
              activities: (activityTitlesByDay[d.id] || []).map(title => ({ title })),
            }));

          const res = await authenticatedFetch(`${API_URL}/api/generate-day-activities`, {
            method: "POST",
            body: JSON.stringify({
              tripBrief: tripData?.brief || {},
              wave1Days: wave1DaysPayload,
              wave2MustSees: existingActivities,
              completedDays: completedDays,
              targetDay: targetDayPayload,
              language: getSetting('wanderologie_settings.language') || 'English',
              model: getSetting('wanderologie_settings.aiModel'),
            }),
          });

          if (res.status === 429) {
            const errData = await res.json();
            showToast(errData.message, "error");
            return;
          }

          const data = await res.json();
          if (!res.ok) {
            throw new Error(data.error?.message || `Server error (${res.status})`);
          }

          const activities = data.activities || [];

          // Write activities to Firestore using writeBatch
          if (activities.length > 0) {
            const actBatch = writeBatch(db);
            activities.forEach((act, idx) => {
              const actId = doc(collection(db, "activities")).id;
              const actPayload = Object.fromEntries(
                Object.entries({
                  id: actId,
                  tripId,
                  ownerUid: auth.currentUser.uid,
                  dayId: newId,
                  planType: "primary",
                  order: idx + 1,
                  aiGenerated: true,
                  locked: false,
                  confirmed: false,
                  version: 1,
                  title: act.title || "Untitled",
                  detail: act.detail || null,
                  category: VALID_CATEGORIES.includes(act.category) ? act.category : "leisure",
                  timeBlock: act.timeBlock || "morning",
                  cost: act.cost ?? null,
                  duration: act.duration || null,
                  bookingStatus: VALID_BOOKING_STATUSES.includes(act.bookingStatus) ? act.bookingStatus : "not_needed",
                  bookingLeadDays: act.bookingLeadDays ?? null,
                  aiRationale: act.aiRationale || null,
                  location: act.location || null,
                  img: null,
                  imgCropParams: null,
                  notes: null,
                  previousVersion: null,
                }).filter(([_, v]) => v !== undefined)
              );
              actBatch.set(doc(db, "activities", actId), actPayload);
            });
            await actBatch.commit();
          }

          // Update day doc with country + flag from AI response
          const aiCountry = data.country || "";
          const aiFlag = aiCountry ? countryToFlag(aiCountry, data.countryCode) : "";
          await setDoc(doc(db, "days", newId), {
            country: aiCountry,
            flag: aiFlag,
            aiInitialPassComplete: true,
          }, { merge: true });

          // Update local state
          setDays(prev => prev.map(d =>
            d.id === newId ? { ...d, country: aiCountry, flag: aiFlag, aiInitialPassComplete: true } : d
          ));

          showToast(`Activities generated for ${cityCapitalized}`, "success");
        } catch (aiErr) {
          console.error("[ItineraryView] AI generation failed:", aiErr);
          showToast("Day added — AI generation failed: " + aiErr.message, "error");
        }
      } else {
        showToast(`Day added — ${cityCapitalized}`, "success");
      }

      const newIndex = effectiveOrder - 1;
      setShowAddDay(false);
      setAddDayClosing(false);
      setAddDayForm({ city: "" });
      setAddDayMode("blank");
      setAddDayPrompt("");
      setInsertPosition(null);
      setTimeout(() => goDay(newIndex), 100);
    } catch (e) {
      console.error("[ItineraryView] Failed to add day:", e);
      showToast("Failed to add day — " + e.message, "error");
    } finally {
      setAddDayLoading(false);
    }
  };

  // Gate: fetch error
  if (fetchError) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100dvh", padding: 24,
        fontFamily: FONT.serif, color: COLOR.inkDark, background: COLOR.cream,
      }}>
        <p style={{ fontSize: 18, marginBottom: 16 }}>Something went wrong loading this trip.</p>
        <button
          onClick={() => window.location.href = "/"}
          style={{
            padding: "10px 24px", background: COLOR.mauve, color: COLOR.white,
            border: "none", borderRadius: 8, fontFamily: FONT.serif, fontSize: 15, cursor: "pointer",
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Gate: if trip is still in creation flow, show skeleton approval screen
  if (tripNotFound) {
    return (
      <div style={{
        minHeight: "100vh", background: COLOR.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column",
      }}>
        <div style={{ fontFamily: FONT.display, fontSize: 24, fontWeight: 300, color: COLOR.inkDark, marginBottom: 8 }}>
          This trip doesn't exist — or it was deleted.
        </div>
        <div style={{ fontFamily: FONT.mono, fontSize: 14, color: COLOR.inkMid, marginBottom: 24 }}>
          It might have been removed, or the link is wrong.
        </div>
        <button
          onClick={() => navigate("/")}
          style={{
            background: COLOR.mauve, color: COLOR.white, border: "none",
            padding: "10px 24px", borderRadius: 8,
            fontFamily: FONT.mono, fontSize: 14, cursor: "pointer",
          }}
        >
          Back to trips
        </button>
      </div>
    );
  }

  if (tripStatusLoading) {
    return (
      <div style={{ minHeight: "100vh", background: COLOR.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 13, color: COLOR.inkMid, letterSpacing: "0.1em" }}>Loading trip…</span>
      </div>
    );
  }
  if (
    ["wave1_complete", "wave2_complete"].includes(tripStatus) ||
    (tripStatus === "creating" && !tripData?.wave3Started)
  ) {
    return <TripSkeletonApproval tripStatus={tripStatus} onApproved={() => {
      setTripStatus("ready");
      setDaysRefreshKey(k => k + 1);
    }} />;
  }

  if (daysLoading) {
    return (
      <div style={{ minHeight: "100vh", background: COLOR.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 13, color: COLOR.inkMid, letterSpacing: "0.1em" }}>Loading trip…</span>
      </div>
    );
  }

  if (daysError || days.length === 0) {
    return (
      <div style={{ minHeight: "100vh", background: COLOR.cream, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 13, color: COLOR.mauveDark }}><Icon name="warning" size={14} color={COLOR.mauveDark} style={{ marginRight: 4, verticalAlign: "middle" }} /> {daysError || "No days found for this trip."}</span>
        <button onClick={() => navigate("/")} style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, padding: "10px 18px", cursor: "pointer", fontFamily: FONT.mono, fontSize: 12 }}><Icon name="back" size={14} /> Back</button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: COLOR.cream,
      fontFamily: FONT.serif, color: COLOR.inkDark,
      display: "flex", flexDirection: "column", maxWidth: "100vw", overflowX: "hidden",
    }}>
      <style>{GLOBAL_CSS}{`
@keyframes skeletonPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.25; }
}
      `}</style>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      {/* Navbar */}
      <div data-testid="itinerary-header" style={{
        padding: "0 18px",
        paddingTop: "max(14px, env(safe-area-inset-top))",
        paddingBottom: 12,
        borderBottom: `1px solid ${COLOR.borderWarm}`,
        background: COLOR.white, flexShrink: 0,
        boxShadow: `0 1px 0 ${COLOR.borderWarm}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: !isDesktop ? "wrap" : "nowrap", gap: !isDesktop ? 8 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
            <button
              onClick={() => navigate("/")}
              style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, width: 44, height: 44, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            >
              <Icon name="back" size={18} />
            </button>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.14em", color: COLOR.inkMid, fontFamily: FONT.mono, textTransform: "uppercase", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {(Array.isArray(cfg.travelers) ? cfg.travelers : []).join(" & ")}{cfg.departureCity ? ` · ${cfg.departureCity}` : ""}
              </div>
              <div data-testid="itinerary-title" style={{ fontSize: 17, fontWeight: 400, color: COLOR.inkDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tripData?.title || tripId}{cfg.startDate && cfg.endDate ? ` · ${formatHeaderDate(cfg.startDate)} – ${formatHeaderDate(cfg.endDate)}` : ""}</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {getSetting('wanderologie_settings.showAvantLeDepart') !== false && (
            <span
              onClick={() => navigate(`/trip/${tripId}/avant-le-depart`)}
              style={{ fontSize: 11, fontFamily: FONT.mono, color: COLOR.inkMid, letterSpacing: "0.08em", cursor: "pointer", textDecoration: "none", ...(!isDesktop ? { maxWidth: 75, textAlign: "right", lineHeight: 1.4 } : { whiteSpace: "nowrap" }) }}
            >
              Avant le Départ →
            </span>
            )}
            <TripOverflowMenu
              onEditTrip={() => setShowTripEdit(true)}
              onExportPDF={() => handleExport('pdf')}
              onExportWord={() => handleExport('docx')}
            />
          </div>
        </div>
      </div>

      {/* Day picker */}
      <DayPicker days={daysWithDates} selDay={selDay} goDay={goDay} pickerRef={pickerRef} pillRefs={pillRefs} onAddDay={(pos) => { setInsertPosition(pos ?? null); setShowAddDay(true); }} activityCounts={activityCounts} onMoveDay={handleMoveDay} onDeleteDay={handleDeleteDay} />

      {/* Add Day sheet */}
      {showAddDay && (
        <div
          onClick={closeAddDay}
          style={{
            position: "fixed", inset: 0, zIndex: 300,
            display: "flex", alignItems: "flex-end",
            background: addDayClosing ? `${COLOR.inkDark}00` : `${COLOR.inkDark}80`,
            transition: "background 0.26s",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: COLOR.cream, borderRadius: "24px 24px 0 0",
              width: "100%", maxWidth: 680,
              maxHeight: "92vh", overflowY: "auto", WebkitOverflowScrolling: "touch",
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
              animation: `${addDayClosing ? "slideDown" : "slideUp"} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
            }}
          >
            {/* Drag handle */}
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: COLOR.borderWarm }} />
            </div>

            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 20px 14px", borderBottom: `1px solid ${COLOR.borderWarm}`,
            }}>
              <div>
                <div style={{ fontSize: 10, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.12em", marginBottom: 3 }}>
                  ＋ ADD A DAY
                </div>
                <div style={{ fontSize: 16, color: COLOR.inkDark, fontFamily: FONT.serif, lineHeight: 1.2 }}>
                  New Day
                </div>
              </div>
              <button
                onClick={closeAddDay}
                style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, width: 44, height: 44, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            {/* Form */}
            <div style={{ padding: "20px 20px 16px" }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5, display: "block" }}>CITY</label>
                <input
                  value={addDayForm.city}
                  onChange={e => setAddDayForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="e.g. Copenhagen"
                  style={{ width: "100%", boxSizing: "border-box", background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 8, padding: "10px 12px", color: COLOR.inkDark, fontSize: 14, fontFamily: FONT.serif, outline: "none", transition: "border-color 0.15s", textTransform: "capitalize" }}
                  onFocus={e => e.target.style.borderColor = COLOR.mauve}
                  onBlur={e => e.target.style.borderColor = COLOR.borderWarm}
                />
              </div>

              {/* Mode selector */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button
                  onClick={() => setAddDayMode("blank")}
                  style={{
                    flex: 1, borderRadius: 10, padding: "10px", fontSize: 13, fontFamily: FONT.mono, minHeight: 44, cursor: "pointer",
                    background: addDayMode === "blank" ? COLOR.mauve : COLOR.buttonSecondary,
                    color: addDayMode === "blank" ? COLOR.white : COLOR.inkMid,
                    border: addDayMode === "blank" ? "none" : `1px solid ${COLOR.borderWarm}`,
                  }}
                >
                  Blank Day
                </button>
                <button
                  onClick={() => setAddDayMode("ai")}
                  style={{
                    flex: 1, borderRadius: 10, padding: "10px", fontSize: 13, fontFamily: FONT.mono, minHeight: 44, cursor: "pointer",
                    background: addDayMode === "ai" ? COLOR.mauve : COLOR.buttonSecondary,
                    color: addDayMode === "ai" ? COLOR.white : COLOR.inkMid,
                    border: addDayMode === "ai" ? "none" : `1px solid ${COLOR.borderWarm}`,
                  }}
                >
                  <Icon name="aiSparkle" size={14} style={{ marginRight: 4, verticalAlign: "middle" }} /> AI Generate
                </button>
              </div>

              {addDayMode === "ai" && (
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    value={addDayPrompt}
                    onChange={e => setAddDayPrompt(e.target.value)}
                    placeholder="e.g. A relaxed beach day, maybe some local markets"
                    rows={3}
                    style={{ width: "100%", boxSizing: "border-box", background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 8, padding: "10px 12px", color: COLOR.inkDark, fontSize: 14, fontFamily: FONT.serif, outline: "none", transition: "border-color 0.15s", resize: "vertical", lineHeight: 1.6 }}
                    onFocus={e => e.target.style.borderColor = COLOR.mauve}
                    onBlur={e => e.target.style.borderColor = COLOR.borderWarm}
                  />
                </div>
              )}

              <button
                onClick={handleAddDay}
                disabled={addDayLoading}
                style={{
                  width: "100%", border: "none", borderRadius: 12, color: COLOR.white,
                  padding: "14px", fontSize: 15, fontFamily: FONT.mono,
                  minHeight: 52, cursor: addDayLoading ? "default" : "pointer",
                  background: addDayLoading ? COLOR.borderWarm : COLOR.mauve,
                  transition: "background 0.2s", marginBottom: 8,
                }}
              >
                {addDayLoading ? (addDayMode === "ai" ? "Generating activities…" : "Adding…") : "Add Day"}
              </button>
              <button
                onClick={closeAddDay}
                style={{
                  width: "100%", background: "transparent", border: "none",
                  color: COLOR.inkMid, fontSize: 13, fontFamily: FONT.mono,
                  cursor: "pointer", padding: "8px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Swipeable content — key resets DayView local state on day change */}
      <div
        style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}
        onTouchStart={onTS}
        onTouchEnd={onTE}
      >
        <DayView
          key={`${selDay}-${daysRefreshKey}`}
          day={day}
          selDay={selDay}
          days={days}
          goDay={goDay}
          onDayUpdate={handleDayUpdate}
          onDeleteDay={handleDeleteDay}
          tripData={tripData}
          onRegisterFlush={fn => { flushNotesRef.current = fn; }}
          tripStatus={tripStatus}
          onCompleteDay={handleCompleteDay}
          activityTitlesByDay={activityTitlesByDay}
        />
        {/* Skeleton placeholder cards for pending days (Change 5b) */}
        {dayIsPending && (
          <div style={{ padding: "0 18px 24px" }}>
            {[0, 1].map(i => (
              <div
                key={i}
                style={{
                  height: 80,
                  borderRadius: 12,
                  background: COLOR.buttonSecondary,
                  opacity: 0.5,
                  marginBottom: 8,
                  animation: 'skeletonPulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
            <div style={{
              fontFamily: 'monospace', fontSize: 11,
              color: COLOR.inkMid, textAlign: 'center',
              letterSpacing: '0.06em', marginTop: 8,
            }}>
              Generating activities…
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "10px 18px", paddingBottom: "max(10px, env(safe-area-inset-bottom))",
        borderTop: `1px solid ${COLOR.borderWarm}`, background: COLOR.white, flexShrink: 0,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => goDay(Math.max(0, selDay - 1))}
            disabled={selDay === 0}
            style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: selDay === 0 ? COLOR.dragHandle : COLOR.inkMid, width: 44, height: 44, cursor: selDay === 0 ? "default" : "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="back" size={18} />
          </button>
          <button
            onClick={() => goDay(Math.min(days.length - 1, selDay + 1))}
            disabled={selDay === days.length - 1}
            style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: selDay === days.length - 1 ? COLOR.dragHandle : COLOR.inkMid, width: 44, height: 44, cursor: selDay === days.length - 1 ? "default" : "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="forward" size={18} />
          </button>
        </div>
        <button
          data-testid="history-button"
          onClick={() => setShowHistory(true)}
          style={{
            marginLeft: "auto",
            background: "none",
            border: "none",
            cursor: "pointer",
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 10,
          }}
        >
          <Icon name="unbook" size={18} color={COLOR.inkMid} />
        </button>
      </div>
      {showHistory && (
        <VersionHistory
          tripId={tripId}
          db={db}
          days={days}
          activities={[]}
          onRestore={(restoredDays) => {
            setDays(restoredDays);
            setDaysRefreshKey(k => k + 1);
          }}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showTripEdit && (
        <TripEditPanel
          tripData={tripData}
          slug={tripId}
          days={days}
          onClose={() => setShowTripEdit(false)}
          onSave={async () => {
            try {
              const snap = await getDoc(doc(db, "trips", tripId));
              if (snap.exists()) {
                setTripData(snap.data());
              }
            } catch (e) {
              console.error("[ItineraryView] Failed to re-fetch trip after edit:", e);
            }
          }}
        />
      )}
    </div>
  );
}

// Format ISO date (YYYY-MM-DD) or human-readable string for header display
function formatHeaderDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s.includes("T") ? s : s + "T00:00:00Z");
    if (isNaN(d)) return s;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  } catch { return s; }
}
